import { useEffect } from "react";
import { useHistory } from "react-router";
import { useToast } from "App/common/useToast";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { SpaceRelatedWatcherIds } from "App/store/watchers/useWatcherV2";
import { useMyMemberships } from "./MyMembershipsContext";

/**
 * Cooperative teardown. This is the user experience, and not a security
 * check.
 *
 * By the time this runs, the bytes are already on the client: the track
 * audio is in the media element's buffer, the comments are in React state,
 * and `getFileUrl` hands out a signed URL with no authorization check at
 * all. A client that simply does not run this code keeps everything it
 * already has.
 *
 * The enforcement half is server-side, is not this hook, and is not written
 * yet. It is the per-content `aclRev` stamp: a revision number on each
 * content document, which makes Firestore re-evaluate the rules on the
 * content documents themselves. The entity types declare the field, and
 * nothing bumps it yet. A revision stamp on the space document cannot do
 * that job. Firestore re-evaluates a listener's rules when the listened
 * document changes, not when a document that the rules `get()` changes. So a
 * bump of `membersRev` on `spaces-v1/{id}` reaches the listeners on that
 * space document and nothing else. Tracks, comments, and files do not see
 * it.
 */

/**
 * Every `componentId` that can hold a watcher keyed by a space id.
 *
 * Warning: a plain `SpaceRelatedWatcherIds[]` array literal gives no
 * exhaustiveness check (no check that the list is complete): an array that
 * misses a member still compiles. We need this claim: when someone adds a
 * new watcher id, the compiler forces an entry here. Only a mapped object
 * gives that, because TypeScript requires the keys of this `Record` type to
 * be total. That requirement is the whole point of this declaration.
 */
const SPACE_RELATED_COMPONENT_ID_SET: Record<SpaceRelatedWatcherIds, true> = {
  "space-home": true,
  "space-home-child-item-rows": true,
  "space-picker-all-spaces": true,
  "space-picker-pinned-space": true,
  "space-navigation": true,
  "space-global-audio-player": true,
  "share-dialog": true,
  "click-thru-space-row": true,
  "space-track-editor": true,
  "breadcrumbs-space-home": true,
  "breadcrumbs-space-track": true,
};

export const SPACE_RELATED_COMPONENT_IDS = Object.keys(
  SPACE_RELATED_COMPONENT_ID_SET,
) as SpaceRelatedWatcherIds[];

export const useRevocationTeardown = () => {
  const { revokedSpaceIds, acknowledgeRevoked } = useMyMemberships();
  const { membersV2Enabled } = useMembershipFlags();
  const { globalSpacesDispatch } = useGlobalSpaces();
  const { manageGlobalChildEntityWatchers } = useChildEntityWatchers();
  const { spaceId: openSpaceId } = useUrlContext();
  const history = useHistory();
  const { addErrorMessage } = useToast();

  useEffect(() => {
    if (!membersV2Enabled || revokedSpaceIds.size < 1) return;

    revokedSpaceIds.forEach((revokedSpaceId) => {
      /* 1. Evict the space from the global store.
       *
       * `REMOVE_SPACE`'s payload is `Space["id"]`, a bare string, and the
       * reducer does `newSpaces.delete(action.payload)`. If you dispatch an
       * object here, `tsc` fails. A wider action type that makes it compile
       * also makes `Map.delete({…})` delete nothing, and then the eviction
       * silently does not happen. */
      globalSpacesDispatch({ type: "REMOVE_SPACE", payload: revokedSpaceId });

      /* 2. Detach the child watchers, for every component id, not one.
       *
       * `useWatcherV2` ref-counts by `componentId` (it keeps a set of the
       * components attached to each watcher): a detach removes only that
       * component from the set, and it unsubscribes only when the set
       * empties. `manageGlobalChildEntityWatchers({ spaceId, componentId })`
       * closes over exactly one id. So a detach of just "space-home" leaves
       * the same space's watchers alive under "space-picker-all-spaces",
       * "space-home-child-item-rows", and "share-dialog".
       *
       * This is safe to run in every case. `DETACH_LISTENER` returns the
       * state unchanged when the key is absent, so a repeat call changes
       * nothing. The set is ref-counted, so a detach of an id that a live,
       * still-authorized component holds is wrong in general. But we only
       * reach this line for a space the identity lost. Any component still
       * mounted on it either renders stale data already or is about to
       * unmount in step 3.
       *
       * Know what this does not do, and must not pretend to do: it stops the
       * client from a re-subscribe. It does not stop an in-flight
       * `onSnapshot` callback, does not evict React state that is already
       * rendered, and does not touch the audio buffer. */
      SPACE_RELATED_COMPONENT_IDS.forEach((componentId) => {
        manageGlobalChildEntityWatchers({
          spaceId: revokedSpaceId,
          componentId,
        }).detach();
      });

      /* 3. If the user looks at this space right now, move them off it.
       *
       * Only then: to navigate a user away from a page they are not on is a
       * worse bug than to leave a stale listener open, and `revokedSpaceIds`
       * can hold several spaces at once after a bulk removal. */
      if (openSpaceId && openSpaceId === revokedSpaceId) {
        addErrorMessage("Your access to this space was removed.");
        history.replace("/library");
      }

      /* 4. Acknowledge, so a re-grant in the same session is a fresh event
       * and this effect does not re-run on every later snapshot. */
      acknowledgeRevoked(revokedSpaceId);
    });
  }, [membersV2Enabled, revokedSpaceIds, openSpaceId]);
};
